import { Link as RouterLink, useRouteMatch } from "react-router-dom";
import { useState } from "react";
import { useSelector } from "react-redux";
import { Menu, X, ChevronDown, Search, Check, ShoppingCart } from "lucide-react";
import AccountDropdown from "components/appHeader/AccountDropdown";
import { Button } from "../ui/button";
import { BoroughSelect } from "./borough-select";
import { PredictionList, BlockLotResults } from "./search-results";
import { useAddressSearch, useBlockLotSearch } from "../../hooks/useSiteSearch";
import { NAV_LINKS } from "../../lib/site";
import { asset } from "../../lib/assets";
import { cn } from "../../lib/utils";

const logoAsset = { url: asset("violerts-logo-01.png") };
const MODE_LABELS = {
    address: "Address",
    bbl: "BBL",
};

// react-router v5 has no `activeProps`; this keeps the Lovable markup as-is.
function Link({ activeProps, className, to, ...rest }) {
    const match = useRouteMatch({ path: to });
    return <RouterLink to={to} className={cn(className, match && activeProps?.className)} {...rest}/>;
}

function SearchSubmit({ className, loading }) {
    return (<button type="submit" aria-label="Search" disabled={loading} className={className}>
      <Search className="h-4 w-4" aria-hidden="true"/>
    </button>);
}

// Address / BBL search box. `mobile` renders the stacked layout used in the collapsed menu.
function HeaderSearch({ mobile = false }) {
    const [searchMode, setSearchMode] = useState("address");
    const [modeMenuOpen, setModeMenuOpen] = useState(false);
    const address = useAddressSearch();
    const blockLot = useBlockLotSearch();
    const idSuffix = mobile ? "-mobile" : "";

    const submitSearch = (e) => {
        e.preventDefault();
        setModeMenuOpen(false);
        if (searchMode === "bbl") {
            blockLot.search();
        }
    };

    const modeDropdown = () => (<div className="relative shrink-0 h-full">
      <button type="button" aria-haspopup="listbox" aria-expanded={modeMenuOpen} aria-label="Search mode" onClick={() => setModeMenuOpen((v) => !v)} className={`flex h-full items-center gap-1 rounded-l-md border-r border-border bg-muted/60 px-2.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground transition-colors hover:text-foreground ${mobile ? "" : "gap-0.5 px-2"}`}>
        {MODE_LABELS[searchMode]}
        <ChevronDown className={`h-2.5 w-2.5 transition-transform ${modeMenuOpen ? "rotate-180" : ""}`}/>
      </button>
      {modeMenuOpen && (<>
          <button type="button" aria-hidden="true" tabIndex={-1} className="fixed inset-0 z-40 cursor-default" onClick={() => setModeMenuOpen(false)}/>
          <div role="listbox" aria-label="Search mode options" className="absolute left-0 top-full z-50 mt-2 w-40 overflow-hidden rounded-lg border border-border bg-popover p-1 shadow-lg">
            {["address", "bbl"].map((mode) => (<button key={mode} type="button" role="option" aria-selected={searchMode === mode} onClick={() => {
                    setSearchMode(mode);
                    setModeMenuOpen(false);
                }} className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm transition-colors ${searchMode === mode
                    ? "bg-secondary font-semibold text-foreground"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
                {mode === "address" ? "Address" : "BBL (Block / Lot)"}
                {searchMode === mode && <Check className="h-3.5 w-3.5 text-signal"/>}
              </button>))}
          </div>
        </>)}
    </div>);

    const addressInput = (inputClassName) => (<>
      <label className="sr-only" htmlFor={`header-address-search${idSuffix}`}>
        Search an address
      </label>
      <input id={`header-address-search${idSuffix}`} name="address" type="search" role="combobox" aria-expanded={address.showList} aria-controls={`header-address-list${idSuffix}`} aria-autocomplete="list" placeholder="Search an address" autoComplete="off" value={address.value} onChange={address.onChange} onKeyDown={address.onKeyDown} onFocus={address.onFocus} className={inputClassName}/>
      <PredictionList search={address} id={`header-address-list${idSuffix}`} className="left-0 right-0"/>
    </>);

    const blockInput = (cls) => (<input id={`header-block-search${idSuffix}`} name="block" type="text" inputMode="numeric" placeholder="Block" value={blockLot.block} onChange={(e) => blockLot.setBlock(e.target.value)} className={cls}/>);
    const lotInput = (cls) => (<input id={`header-lot-search${idSuffix}`} name="lot" type="text" inputMode="numeric" placeholder="Lot" value={blockLot.lot} onChange={(e) => blockLot.setLot(e.target.value)} className={cls}/>);

    if (!mobile) {
        return (<form role="search" onSubmit={submitSearch} ref={searchMode === "bbl" ? blockLot.containerRef : address.containerRef} className="relative flex h-9 min-w-0 items-center gap-1.5 rounded-lg border border-border bg-muted/40 pr-0.5 xl:w-[26rem] lg:w-[21rem]">
        {modeDropdown()}
        {searchMode === "address" ? (addressInput("h-full min-w-0 flex-1 bg-transparent px-1 text-sm outline-none placeholder:text-muted-foreground")) : (<>
            <div className="flex min-w-0 flex-1 items-center">
              <label className="sr-only" htmlFor="header-block-search">
                Block
              </label>
              {blockInput("h-full w-14 shrink-0 bg-transparent text-center text-sm outline-none placeholder:text-muted-foreground")}
              <span className="h-5 w-px shrink-0 bg-border" aria-hidden="true"/>
              <label className="sr-only" htmlFor="header-lot-search">
                Lot
              </label>
              {lotInput("h-full w-14 shrink-0 bg-transparent text-center text-sm outline-none placeholder:text-muted-foreground")}
              <span className="h-5 w-px shrink-0 bg-border" aria-hidden="true"/>
              <label className="sr-only" htmlFor="header-borough-search">
                Borough
              </label>
              <BoroughSelect id="header-borough-search" value={blockLot.borough} onValueChange={blockLot.setBorough} className="h-8 min-w-0 flex-1 rounded-none border-0 bg-transparent px-2 text-xs text-muted-foreground"/>
            </div>
            <BlockLotResults search={blockLot}/>
          </>)}
        <SearchSubmit loading={blockLot.loading} className="my-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-signal text-ink transition-opacity hover:opacity-90"/>
      </form>);
    }

    return (<form role="search" onSubmit={submitSearch} ref={searchMode === "bbl" ? blockLot.containerRef : address.containerRef} className={`relative mb-4 flex items-center gap-2 rounded-lg border border-border bg-muted/40 py-2 pl-1 pr-1 ${searchMode === "bbl" ? "flex-wrap" : "h-11"}`}>
      {modeDropdown()}
      {searchMode === "address" ? (<>
          {addressInput("h-7 min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground")}
          <SearchSubmit className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-signal text-ink transition-opacity hover:opacity-90"/>
        </>) : (<>
          <div className="ml-auto flex flex-1 justify-end">
            <SearchSubmit loading={blockLot.loading} className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-signal text-ink transition-opacity hover:opacity-90"/>
          </div>
          <div className="flex w-full items-center gap-2 pt-1">
            <label className="sr-only" htmlFor="header-block-search-mobile">
              Block
            </label>
            {blockInput("h-9 w-16 shrink-0 rounded-md border border-border bg-background px-2 text-sm outline-none placeholder:text-muted-foreground")}
            <label className="sr-only" htmlFor="header-lot-search-mobile">
              Lot
            </label>
            {lotInput("h-9 min-w-0 flex-1 rounded-md border border-border bg-background px-2 text-sm outline-none placeholder:text-muted-foreground")}
            <label className="sr-only" htmlFor="header-borough-search-mobile">
              Borough
            </label>
            <BoroughSelect id="header-borough-search-mobile" value={blockLot.borough} onValueChange={blockLot.setBorough} className="h-9 w-32 shrink-0 border-border bg-background px-2 text-xs text-muted-foreground"/>
          </div>
          <BlockLotResults search={blockLot}/>
        </>)}
    </form>);
}

// Cart shortcut with the item count (same as the dashboard header's cart link).
function CartLink() {
    const cartCount = useSelector((state) => state.cart?.items?.length ?? 0);
    return (<Link to="/cart" aria-label="Cart" className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
      <ShoppingCart className="h-4 w-4" aria-hidden="true"/>
      {cartCount > 0 && (<span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
          {cartCount}
        </span>)}
    </Link>);
}

// Cart + account. Visitors get Log in / Sign up; signed-in users get the app's real account menu
// (dashboard, admin, settings, organization switcher, sign out).
function AuthButtons({ fill = false }) {
    const isLoggedIn = useSelector((state) => !!state.user?.userData?.id);
    const grow = fill ? "flex-1" : undefined;
    return (<>
      <CartLink />
      {isLoggedIn ? (<AccountDropdown />) : (<>
          <Button asChild variant={fill ? "outline" : "ghost"} size="sm" className={grow}>
            <Link to="/sign-in">Log in</Link>
          </Button>
          <Button asChild variant="signal" size="sm" className={grow}>
            <Link to="/sign-up">Sign up free</Link>
          </Button>
        </>)}
    </>);
}

// Desktop "Features" menu. Open state is React-driven (hover + keyboard focus) rather than
// CSS `group-hover:visible`, so it cannot be defeated by Bootstrap's !important `.invisible`.
function NavDropdown({ link }) {
    const [open, setOpen] = useState(false);
    return (<div className="relative flex h-full items-center" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)} onFocus={() => setOpen(true)} onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false);
        }} onKeyDown={(e) => {
            if (e.key === "Escape") setOpen(false);
        }}>
      <Link to={link.to} aria-haspopup="true" aria-expanded={open} className={cn("inline-flex h-full items-center gap-1 text-sm font-medium leading-none text-muted-foreground transition-colors hover:text-foreground", open && "text-foreground")} activeProps={{ className: "text-foreground" }}>
        {link.label}
        <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")}/>
      </Link>
      {open && (<div className="absolute left-1/2 top-full z-50 w-72 -translate-x-1/2 pt-3">
          <div className="overflow-hidden rounded-xl border border-border bg-popover p-2 shadow-lg">
            {link.children.map((c) => (<Link key={c.to} to={c.to} onClick={() => setOpen(false)} className="block rounded-lg px-3 py-2.5 transition-colors hover:bg-secondary" activeProps={{ className: "bg-secondary" }}>
                <span className="block text-sm font-semibold">{c.label}</span>
              </Link>))}
          </div>
        </div>)}
    </div>);
}

export function SiteHeader() {
    const [open, setOpen] = useState(false);
    return (<header className="sticky top-0 z-50 border-b border-border/70 bg-background/85 backdrop-blur">
      <div className="flex h-16 items-center justify-between gap-6 px-5">
        <div className="flex items-center gap-8">
          <Link to="/" className="flex items-center" onClick={() => setOpen(false)}>
            <img src={logoAsset.url} alt="Violerts" className="h-12 w-auto"/>
          </Link>

          <nav className="hidden h-9 items-center gap-7 lg:flex">
            {NAV_LINKS.map((l) => "children" in l ? (<NavDropdown key={l.label} link={l}/>) : (<Link key={l.to} to={l.to} className="inline-flex h-full items-center text-sm font-medium leading-none text-muted-foreground transition-colors hover:text-foreground" activeProps={{ className: "text-foreground" }}>
                  {l.label}
                </Link>))}
          </nav>
        </div>

        <div className="hidden items-center gap-2 lg:flex">
          <HeaderSearch />

          <AuthButtons />
        </div>

        <button className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border lg:hidden" aria-label="Toggle menu" onClick={() => setOpen((v) => !v)}>
          {open ? <X className="h-4 w-4"/> : <Menu className="h-4 w-4"/>}
        </button>
      </div>

      {open && (<div className="border-t border-border bg-background px-5 py-4 lg:hidden">
          <HeaderSearch mobile/>
          <nav className="flex flex-col gap-1">
            {NAV_LINKS.map((l) => (<div key={l.label}>
                <Link to={l.to} onClick={() => setOpen(false)} className="block rounded-md px-2 py-2 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground">
                  {l.label}
                </Link>
                {"children" in l && (<div className="ml-3 border-l border-border pl-3">
                    {l.children.map((c) => (<Link key={c.to} to={c.to} onClick={() => setOpen(false)} className="block rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground">
                        {c.label}
                      </Link>))}
                  </div>)}
              </div>))}
          </nav>
          <div className="mt-3 flex gap-2">
            <AuthButtons fill/>
          </div>
        </div>)}
    </header>);
}
